import { LitElement, html, svg, nothing, type CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import styles from './styles.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';

export type RatingSize = 'small' | 'medium' | 'large';

// ── SVG icons ────────────────────────────────────────────────────────────────

const _starFilled = svg`<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;

const _starOutline = svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;

const _cancelIcon = svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`;

/**
 * uwc-rating
 *
 * Star rating component with keyboard navigation and full ARIA support.
 *
 * @attr value    - Current rating (0 – stars). Default: 0.
 * @attr stars    - Total number of stars. Default: 5.
 * @attr cancel   - Show a cancel / clear button. Default: false.
 * @attr readonly - Display-only; no interaction allowed.
 * @attr disabled - Disabled state.
 * @attr size     - small | medium | large. Default: medium.
 *
 * @fires uwc-change - Fired on value change. Detail: { value: number }.
 *
 * @csspart star   - Each star button.
 * @csspart cancel - The cancel (clear) button.
 *
 * @cssprop --uwc-rating-on-color          - Filled-star color. Default: amber.
 * @cssprop --uwc-rating-off-color         - Empty-star color. Default: muted text.
 * @cssprop --uwc-rating-hover-color       - Hover / preview color. Default: amber.
 * @cssprop --uwc-rating-cancel-color      - Cancel icon base color.
 * @cssprop --uwc-rating-cancel-hover-color - Cancel icon hover color.
 * @cssprop --uwc-rating-gap              - Gap between items. Default: 0.125rem.
 */
export class UwcRating extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  private _validation = new FormValidationController(this);

  // ── Props ─────────────────────────────────────────────────────────

  @property({ type: Number, reflect: true }) value    = 0;
  @property({ type: Number })               stars     = 5;
  @property({ type: Boolean, reflect: true }) cancel   = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) invalid  = false;
  @property({ reflect: true })               size: RatingSize = 'medium';

  // ── Hover preview ─────────────────────────────────────────────────

  @state() private _hovered = 0;

  // ── Lifecycle ─────────────────────────────────────────────────────

  override firstUpdated(): void {
    const hasVal = this.value > 0;
    this._validation.syncFormValue(hasVal ? String(this.value) : null);
    this._validation.revalidate(hasVal ? 'v' : '', { required: this.required });
  }

  override updated(changed: Map<PropertyKey, unknown>): void {
    super.updated(changed);
    if (changed.has('required')) {
      this._validation.revalidate(this.value > 0 ? 'v' : '', { required: this.required });
    }
  }

  // ── Actions ───────────────────────────────────────────────────────

  private _select(v: number) {
    if (this.readonly || this.disabled) return;
    this.value = v;
    this._emit(v);
    this._validation.handleChange('v', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(String(v));
  }

  private _clear() {
    if (this.readonly || this.disabled) return;
    this.value = 0;
    this._emit(0);
    this._validation.handleChange('', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(null);
  }

  private _emit(value: number) {
    this.dispatchEvent(new CustomEvent('uwc-change', {
      bubbles: true, composed: true,
      detail: { value },
    }));
  }

  // ── Keyboard navigation ────────────────────────────────────────────

  private _onStarKeydown(e: KeyboardEvent, n: number) {
    if (this.readonly || this.disabled) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(n + 1, this.stars);
      this._select(next);
      this._focusStar(next);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      const prev = n - 1;
      if (prev <= 0 && this.cancel) {
        this._focusCancel();
      } else {
        this._select(Math.max(prev, 1));
        this._focusStar(Math.max(prev, 1));
      }
    }
  }

  private _onCancelKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      this._select(1);
      this._focusStar(1);
    }
  }

  private _focusStar(n: number) {
    const el = this.shadowRoot?.querySelector(`[data-star="${n}"]`) as HTMLElement | null;
    el?.focus();
  }

  private _focusCancel() {
    const el = this.shadowRoot?.querySelector('.uwc-rating__cancel') as HTMLElement | null;
    el?.focus();
  }

  // ── Render ────────────────────────────────────────────────────────

  override render() {
    const items = Array.from({ length: this.stars }, (_, i) => i + 1);
    const preview = this._hovered > 0 ? this._hovered : this.value;

    return html`
      <div
        class="uwc-rating"
        role="radiogroup"
        aria-label="Rating"
        aria-disabled=${this.disabled}
      >
        ${this.cancel ? html`
          <button
            part="cancel"
            class="uwc-rating__cancel"
            type="button"
            aria-label="Clear rating"
            title="Clear rating"
            ?disabled=${this.disabled}
            @click=${this._clear}
            @keydown=${this._onCancelKeydown}
          >${_cancelIcon}</button>
          <span class="uwc-rating__sep" aria-hidden="true"></span>
        ` : nothing}

        ${items.map(n => {
          const filled  = n <= preview;
          const checked = n <= this.value;
          return html`
            <button
              part="star"
              class=${classMap({
                'uwc-rating__star': true,
                'is-preview': filled && !checked,
              })}
              type="button"
              role="radio"
              data-star=${n}
              aria-label="${n} star${n !== 1 ? 's' : ''}"
              aria-checked=${checked}
              ?disabled=${this.disabled}
              tabindex=${n === (this.value || 1) && !this.cancel ? '0' : n === this.value ? '0' : '-1'}
              @click=${() => this._select(n)}
              @mouseenter=${() => { if (!this.readonly && !this.disabled) this._hovered = n; }}
              @mouseleave=${() => { this._hovered = 0; }}
              @keydown=${(e: KeyboardEvent) => this._onStarKeydown(e, n)}
            >${filled ? _starFilled : _starOutline}</button>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-rating': UwcRating; }
}
